import { useAdvancedSearchRequestBuilder } from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import { useAppSelector } from 'src/utils/Hooks';

import { useMemo } from 'react';
import {
  useApplyEntityFilters,
  useRemoveRetailPriceRangeFilters
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { useMainEntityType, useRetailerId } from 'src/utils/Hooks/reduxSelectors';
import useGenericAdvancedSearch from 'src/utils/Hooks/useGenericAdvancedSearch';
import { AppName } from 'sl-api-connector';

import { AdvertisingMetric } from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/AdvertisingConstants';
import { AdMetricIndexNameByEntityType } from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/AdMetric/AdMetricsUtils';

const useAdCampaignDailyMetrics = () => {
  const entityType = useMainEntityType();
  const metricId = 'summary-tile-ad-daily-metric';
  const applyEntityFilters = useApplyEntityFilters();
  const removeRetailPriceRangeFilters = useRemoveRetailPriceRangeFilters('reviews');
  const retailerId = useRetailerId();
  const { startDayId, endDayId } = useAppSelector((state) => state.mainTimePeriod);
  const { startDayId: comparisonStartDayId, endDayId: comparisonEndDayId } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );

  const keyMetricRequestBuilder = useAdvancedSearchRequestBuilder(
    metricId,
    `advertising-${AdMetricIndexNameByEntityType[entityType]}`
  );

  const aggregationBuilder = new AggregationBuilder('retailerId');
  aggregationBuilder
    .addAggregationField('Ad Impressions', AdvertisingMetric.AD_IMPRESSION, 'sum', true)
    .addAggregationField('Ad Clicks', AdvertisingMetric.AD_CLICK, 'sum', true)
    .addAggregationField(
      'CTR %',
      AdvertisingMetric.CTR,
      'computed',
      true,
      '((impressions_sum > 0) ? ((clicks_sum / impressions_sum)) : (0))'
    )
    .addAggregationField('Ad Spend', AdvertisingMetric.AD_SPEND, 'sum', true)
    .addAggregationField(
      'CPC',
      AdvertisingMetric.CPC,
      'computed',
      true,
      '((clicks_sum > 0) ? ((spend_sum / clicks_sum)) : (0))'
    )
    .addAggregationField('Ad Units Sold', AdvertisingMetric.AD_UNITS_SOLD, 'sum', true)
    .addAggregationField(
      'CPA',
      AdvertisingMetric.CPA,
      'computed',
      true,
      ' ((unitsSold_sum > 0) ? ((spend_sum / unitsSold_sum)) : (0))'
    )
    .addAggregationField('Ad Sales', AdvertisingMetric.AD_SALES, 'sum', true)
    .addAggregationField(
      'ACOS',
      AdvertisingMetric.ACOS,
      'computed',
      true,
      '((sales_sum > 0) ? ((spend_sum / sales_sum)) : (0))'
    )
    .addAggregationField(
      'ROAS',
      AdvertisingMetric.ROAS,
      'computed',
      true,
      '((spend_sum > 0) ? ((sales_sum / spend_sum)) : (0))'
    )
    .addAggregationField(
      'Conversion Rate',
      AdvertisingMetric.CONVERSION,
      'computed',
      true,
      '((clicks_sum > 0) ? ((unitsSold_sum / clicks_sum)) : (0))'
    )
    .addAggregationField('Orders', AdvertisingMetric.ORDERS, 'sum', true)
    .addAggregationField('Incremental Sales', AdvertisingMetric.INCREMENTAL_SALES, 'sum', true)
    .addAggregationField(
      'Incremental ROAS',
      AdvertisingMetric.INCREMENTAL_ROAS,
      'computed',
      true,
      '((spend_sum > 0) ? ((brandTotalClicksIncrementalSales_sum / spend_sum)) : (0))'
    )
    .addAggregationField(
      'Incremental Clicks',
      AdvertisingMetric.INCREMENTAL_CLICKS,
      'computed',
      true,
      '((sales_sum > 0) ? (((brandTotalClicksIncrementalSales_sum / sales_sum) * clicks_sum)) : (0))'
    )
    .addAggregationField(
      'Incrementality',
      AdvertisingMetric.INCREMENTALITY,
      'computed',
      true,
      '((sales_sum > 0) ? ((brandTotalClicksIncrementalSales_sum / sales_sum)) : (0))'
    )
    .addConditionRangeFilter('dayId', startDayId, endDayId)
    .addConditionTermFilter('retailerId', [retailerId])
    .clearComparisonRangeFilters()
    .addComparisonRangeFilter('dayId', comparisonStartDayId, comparisonEndDayId);

  keyMetricRequestBuilder
    .setPageNumber(1)
    .setPageSize(10000)
    .setPeriod('year')
    .setDoAggregation(true)
    .setReturnDocuments(false)
    .setRetailerId(retailerId)
    .setProcessDocuments(true)
    .addConditionRangeFilter('dayId', startDayId, endDayId)
    .addConditionTermFilter('isProjected', 'must', [0])
    .setSearchBy('parent')
    .addAggregation(aggregationBuilder.build())
    .apply(applyEntityFilters)
    .removeConditionTermFilters((builderFilter) => builderFilter.fieldName === 'categoryId')
    .apply(removeRetailPriceRangeFilters);

  const keyMetricWeeklyRequest = keyMetricRequestBuilder.build();

  const { data, isLoading: keyMetricLoading } = useGenericAdvancedSearch({
    requestId: metricId,
    queryKeys: [keyMetricWeeklyRequest],
    requestBody: [keyMetricWeeklyRequest],
    appNameOverride: AppName.Advertising
  });

  const adCampaignDailyMetrics = useMemo(() => {
    return data ? data.data[0].aggregations.by_retailerId : null;
  }, [data]);

  return { adCampaignDailyMetrics, keyMetricLoading };
};

const useAdCampaignAdGroupTargetDailyMetrics = () => {
  const metricId = 'summary-tile-ad-group-target-metric';
  const applyEntityFilters = useApplyEntityFilters();
  const removeRetailPriceRangeFilters = useRemoveRetailPriceRangeFilters('reviews');
  const retailerId = useRetailerId();
  const { startDayId, endDayId } = useAppSelector((state) => state.mainTimePeriod);
  const { startDayId: comparisonStartDayId, endDayId: comparisonEndDayId } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );

  const keyMetricRequestBuilder = useAdvancedSearchRequestBuilder(
    metricId,
    'advertising-adCampaignAdGroupProductTargetDailyMetrics'
  );

  const aggregationBuilder = new AggregationBuilder('retailerId');
  aggregationBuilder
    .addAggregationField('Targets', AdvertisingMetric.TARGETS, 'cardinality', true)
    .addConditionRangeFilter('dayId', startDayId, endDayId)
    .addConditionTermFilter('retailerId', [retailerId])
    .addComparisonRangeFilter('dayId', comparisonStartDayId, comparisonEndDayId);

  keyMetricRequestBuilder
    .setPageNumber(1)
    .setPageSize(10000)
    .setPeriod('year')
    .setDoAggregation(true)
    .setReturnDocuments(false)
    .setRetailerId(retailerId)
    .setProcessDocuments(true)
    .addConditionRangeFilter('dayId', startDayId, endDayId)
    .setSearchBy('parent')
    .addAggregation(aggregationBuilder.build())
    .apply(applyEntityFilters)
    .removeConditionTermFilters((builderFilter) => builderFilter.fieldName === 'categoryId')
    .apply(removeRetailPriceRangeFilters);

  const keyMetricWeeklyRequest = keyMetricRequestBuilder.build();

  const { data, isLoading: keyMetricLoading } = useGenericAdvancedSearch({
    requestId: metricId,
    queryKeys: [keyMetricWeeklyRequest],
    requestBody: [keyMetricWeeklyRequest],
    appNameOverride: AppName.Advertising
  });

  const adGroupTargetDailyData = useMemo(() => {
    return data ? data.data[0].aggregations.by_retailerId : null;
  }, [data]);

  return { adGroupTargetDailyData, keyMetricLoading };
};

export const useAdMetricsData = () => {
  const { startDayId: comparisonStartDayId, endDayId: comparisonEndDayId } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );

  const { adCampaignDailyMetrics, keyMetricLoading: loadingDailyMetric } = useAdCampaignDailyMetrics();

  const { adGroupTargetDailyData, keyMetricLoading: loadingGroupMetric } = useAdCampaignAdGroupTargetDailyMetrics();

  const keyMetricsCardData = {};
  const keyMetricsCompareData = {};

  const keyMetricsCardChanges = {};

  if (adCampaignDailyMetrics?.length > 0) {
    const dailyDataset = adCampaignDailyMetrics[0]?.additionalValues;
    keyMetricsCardData[AdvertisingMetric.AD_IMPRESSION] = dailyDataset[`${AdvertisingMetric.AD_IMPRESSION}_sum_value`];
    keyMetricsCompareData[AdvertisingMetric.AD_IMPRESSION] =
      dailyDataset[`${AdvertisingMetric.AD_IMPRESSION}_sum_value_${comparisonStartDayId}_${comparisonEndDayId}`];
    keyMetricsCardChanges[AdvertisingMetric.AD_IMPRESSION] =
      (dailyDataset[`${AdvertisingMetric.AD_IMPRESSION}_sum_value`] -
        dailyDataset[`${AdvertisingMetric.AD_IMPRESSION}_sum_value_${comparisonStartDayId}_${comparisonEndDayId}`]) /
      dailyDataset[`${AdvertisingMetric.AD_IMPRESSION}_sum_value_${comparisonStartDayId}_${comparisonEndDayId}`];

    keyMetricsCardData[AdvertisingMetric.AD_CLICK] = dailyDataset[`${AdvertisingMetric.AD_CLICK}_sum_value`];
    keyMetricsCompareData[AdvertisingMetric.AD_CLICK] =
      dailyDataset[`${AdvertisingMetric.AD_CLICK}_sum_value_${comparisonStartDayId}_${comparisonEndDayId}`];
    keyMetricsCardChanges[AdvertisingMetric.AD_CLICK] =
      (dailyDataset[`${AdvertisingMetric.AD_CLICK}_sum_value`] -
        dailyDataset[`${AdvertisingMetric.AD_CLICK}_sum_value_${comparisonStartDayId}_${comparisonEndDayId}`]) /
      dailyDataset[`${AdvertisingMetric.AD_CLICK}_sum_value_${comparisonStartDayId}_${comparisonEndDayId}`];

    keyMetricsCardData[AdvertisingMetric.CTR] = dailyDataset[`${AdvertisingMetric.CTR}_computed_value`];
    keyMetricsCompareData[AdvertisingMetric.CTR] =
      dailyDataset[`${AdvertisingMetric.CTR}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`];
    keyMetricsCardChanges[AdvertisingMetric.CTR] =
      (dailyDataset[`${AdvertisingMetric.CTR}_computed_value`] -
        dailyDataset[`${AdvertisingMetric.CTR}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`]) /
      dailyDataset[`${AdvertisingMetric.CTR}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`];

    keyMetricsCardData[AdvertisingMetric.AD_SPEND] = dailyDataset[`${AdvertisingMetric.AD_SPEND}_sum_value`];
    keyMetricsCompareData[AdvertisingMetric.AD_SPEND] =
      dailyDataset[`${AdvertisingMetric.AD_SPEND}_sum_value_${comparisonStartDayId}_${comparisonEndDayId}`];
    keyMetricsCardChanges[AdvertisingMetric.AD_SPEND] =
      (dailyDataset[`${AdvertisingMetric.AD_SPEND}_sum_value`] -
        dailyDataset[`${AdvertisingMetric.AD_SPEND}_sum_value_${comparisonStartDayId}_${comparisonEndDayId}`]) /
      dailyDataset[`${AdvertisingMetric.AD_SPEND}_sum_value_${comparisonStartDayId}_${comparisonEndDayId}`];

    keyMetricsCardData[AdvertisingMetric.CPC] = dailyDataset[`${AdvertisingMetric.CPC}_computed_value`];
    keyMetricsCompareData[AdvertisingMetric.CPC] =
      dailyDataset[`${AdvertisingMetric.CPC}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`];
    keyMetricsCardChanges[AdvertisingMetric.CPC] =
      (dailyDataset[`${AdvertisingMetric.CPC}_computed_value`] -
        dailyDataset[`${AdvertisingMetric.CPC}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`]) /
      dailyDataset[`${AdvertisingMetric.CPC}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`];

    keyMetricsCardData[AdvertisingMetric.CPA] = dailyDataset[`${AdvertisingMetric.CPA}_computed_value`];
    keyMetricsCompareData[AdvertisingMetric.CPA] =
      dailyDataset[`${AdvertisingMetric.CPA}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`];
    keyMetricsCardChanges[AdvertisingMetric.CPA] =
      (dailyDataset[`${AdvertisingMetric.CPA}_computed_value`] -
        dailyDataset[`${AdvertisingMetric.CPA}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`]) /
      dailyDataset[`${AdvertisingMetric.CPA}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`];

    keyMetricsCardData[AdvertisingMetric.ACOS] = dailyDataset[`${AdvertisingMetric.ACOS}_computed_value`];
    keyMetricsCompareData[AdvertisingMetric.ACOS] =
      dailyDataset[`${AdvertisingMetric.ACOS}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`];
    keyMetricsCardChanges[AdvertisingMetric.ACOS] =
      (dailyDataset[`${AdvertisingMetric.ACOS}_computed_value`] -
        dailyDataset[`${AdvertisingMetric.ACOS}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`]) /
      dailyDataset[`${AdvertisingMetric.ACOS}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`];

    keyMetricsCardData[AdvertisingMetric.AD_SALES] = dailyDataset[`${AdvertisingMetric.AD_SALES}_sum_value`];
    keyMetricsCompareData[AdvertisingMetric.AD_SALES] =
      dailyDataset[`${AdvertisingMetric.AD_SALES}_sum_value_${comparisonStartDayId}_${comparisonEndDayId}`];
    keyMetricsCardChanges[AdvertisingMetric.AD_SALES] =
      (dailyDataset[`${AdvertisingMetric.AD_SALES}_sum_value`] -
        dailyDataset[`${AdvertisingMetric.AD_SALES}_sum_value_${comparisonStartDayId}_${comparisonEndDayId}`]) /
      dailyDataset[`${AdvertisingMetric.AD_SALES}_sum_value_${comparisonStartDayId}_${comparisonEndDayId}`];

    keyMetricsCardData[AdvertisingMetric.ROAS] = dailyDataset[`${AdvertisingMetric.ROAS}_computed_value`];
    keyMetricsCompareData[AdvertisingMetric.ROAS] =
      dailyDataset[`${AdvertisingMetric.ROAS}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`];
    keyMetricsCardChanges[AdvertisingMetric.ROAS] =
      (dailyDataset[`${AdvertisingMetric.ROAS}_computed_value`] -
        dailyDataset[`${AdvertisingMetric.ROAS}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`]) /
      dailyDataset[`${AdvertisingMetric.ROAS}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`];

    keyMetricsCardData[AdvertisingMetric.CONVERSION] = dailyDataset[`${AdvertisingMetric.CONVERSION}_computed_value`];
    keyMetricsCompareData[AdvertisingMetric.CONVERSION] =
      dailyDataset[`${AdvertisingMetric.CONVERSION}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`];
    keyMetricsCardChanges[AdvertisingMetric.CONVERSION] =
      (dailyDataset[`${AdvertisingMetric.CONVERSION}_computed_value`] -
        dailyDataset[`${AdvertisingMetric.CONVERSION}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`]) /
      dailyDataset[`${AdvertisingMetric.CONVERSION}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`];

    keyMetricsCardData[AdvertisingMetric.ORDERS] = dailyDataset[`${AdvertisingMetric.ORDERS}_sum_value`];
    keyMetricsCompareData[AdvertisingMetric.ORDERS] =
      dailyDataset[`${AdvertisingMetric.ORDERS}_sum_value_${comparisonStartDayId}_${comparisonEndDayId}`];
    keyMetricsCardChanges[AdvertisingMetric.ORDERS] =
      (dailyDataset[`${AdvertisingMetric.ORDERS}_sum_value`] -
        dailyDataset[`${AdvertisingMetric.ORDERS}_sum_value_${comparisonStartDayId}_${comparisonEndDayId}`]) /
      dailyDataset[`${AdvertisingMetric.ORDERS}_sum_value_${comparisonStartDayId}_${comparisonEndDayId}`];

    keyMetricsCardData[AdvertisingMetric.INCREMENTAL_SALES] =
      dailyDataset[`${AdvertisingMetric.INCREMENTAL_SALES}_sum_value`];
    keyMetricsCompareData[AdvertisingMetric.INCREMENTAL_SALES] =
      dailyDataset[`${AdvertisingMetric.INCREMENTAL_SALES}_sum_value_${comparisonStartDayId}_${comparisonEndDayId}`];
    keyMetricsCardChanges[AdvertisingMetric.INCREMENTAL_SALES] =
      (dailyDataset[`${AdvertisingMetric.INCREMENTAL_SALES}_sum_value`] -
        dailyDataset[
        `${AdvertisingMetric.INCREMENTAL_SALES}_sum_value_${comparisonStartDayId}_${comparisonEndDayId}`
        ]) /
      dailyDataset[`${AdvertisingMetric.INCREMENTAL_SALES}_sum_value_${comparisonStartDayId}_${comparisonEndDayId}`];

    keyMetricsCardData[AdvertisingMetric.INCREMENTAL_ROAS] =
      dailyDataset[`${AdvertisingMetric.INCREMENTAL_ROAS}_computed_value`];
    keyMetricsCompareData[AdvertisingMetric.INCREMENTAL_ROAS] =
      dailyDataset[
      `${AdvertisingMetric.INCREMENTAL_ROAS}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`
      ];
    keyMetricsCardChanges[AdvertisingMetric.INCREMENTAL_ROAS] =
      (dailyDataset[`${AdvertisingMetric.INCREMENTAL_ROAS}_computed_value`] -
        dailyDataset[
        `${AdvertisingMetric.INCREMENTAL_ROAS}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`
        ]) /
      dailyDataset[
      `${AdvertisingMetric.INCREMENTAL_ROAS}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`
      ];

    keyMetricsCardData[AdvertisingMetric.INCREMENTAL_CLICKS] =
      dailyDataset[`${AdvertisingMetric.INCREMENTAL_CLICKS}_computed_value`];

    keyMetricsCompareData[AdvertisingMetric.INCREMENTAL_CLICKS] =
      dailyDataset[
      `${AdvertisingMetric.INCREMENTAL_CLICKS}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`
      ];
    keyMetricsCardChanges[AdvertisingMetric.INCREMENTAL_CLICKS] =
      (dailyDataset[`${AdvertisingMetric.INCREMENTAL_CLICKS}_computed_value`] -
        dailyDataset[
        `${AdvertisingMetric.INCREMENTAL_CLICKS}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`
        ]) /
      dailyDataset[
      `${AdvertisingMetric.INCREMENTAL_CLICKS}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`
      ];

    keyMetricsCardData[AdvertisingMetric.INCREMENTALITY] =
      dailyDataset[`${AdvertisingMetric.INCREMENTALITY}_computed_value`];

    keyMetricsCompareData[AdvertisingMetric.INCREMENTALITY] =
      dailyDataset[`${AdvertisingMetric.INCREMENTALITY}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`];
    keyMetricsCardChanges[AdvertisingMetric.INCREMENTALITY] =
      (dailyDataset[`${AdvertisingMetric.INCREMENTALITY}_computed_value`] -
        dailyDataset[
        `${AdvertisingMetric.INCREMENTALITY}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`
        ]) /
      dailyDataset[`${AdvertisingMetric.INCREMENTALITY}_computed_value_${comparisonStartDayId}_${comparisonEndDayId}`];
  }

  if (adGroupTargetDailyData?.length > 0) {
    const dailyDataset = adGroupTargetDailyData[0]?.additionalValues;
    keyMetricsCardData[AdvertisingMetric.TARGETS] = dailyDataset?.[`${AdvertisingMetric.TARGETS}_cardinality_value`];
    keyMetricsCompareData[AdvertisingMetric.TARGETS] =
      dailyDataset?.[`${AdvertisingMetric.TARGETS}_cardinality_value_${comparisonStartDayId}_${comparisonEndDayId}`];
    keyMetricsCardChanges[AdvertisingMetric.TARGETS] =
      (dailyDataset?.[`${AdvertisingMetric.TARGETS}_cardinality_value`] -
        dailyDataset?.[
        `${AdvertisingMetric.TARGETS}_cardinality_value_${comparisonStartDayId}_${comparisonEndDayId}`
        ]) /
      dailyDataset?.[`${AdvertisingMetric.TARGETS}_cardinality_value_${comparisonStartDayId}_${comparisonEndDayId}`];
  }

  return {
    keyMetricsCardData,
    keyMetricsCompareData,
    keyMetricsCardChanges,
    loading: loadingDailyMetric || loadingGroupMetric
  };
};
